// /materials/ (PrintingProcessPage.js): the 3D printing materials guide.
// Code-owned since 2026-11-04; gatsby-node skips the Contentful entry.
// GSC Aug–Oct 2026: 0 clicks, 89 impressions (Somos resin names). "3d printing
// materials" is 9,900/mo (KD 15); its results are material guides, so this
// is a reference page that sends readers to the process pages.
// Materials are the ones the 3D printing pages already list (truth.md and the
// live SLA, custom and metal pages): standard, Somos, Somos WaterClear,
// tough, castable and high-temperature resins; ABS; Nylon PA12; stainless
// steel. The old page's CNC and casting materials belong to other services
// and are left out. General material behavior is stated without numbers.
const {
  photoRoot,
  reviewedDate,
  studios,
  delivery,
  files,
} = require('./shared')
const printing = require('../printing-service')
const sla = require('./sla')
const metal = require('./metal')

const page = {
  path: '/materials/',
  crumb: '3D printing materials',
  title: '3D Printing Materials Guide | Resin, ABS, Nylon & Steel',
  description:
    '3D printing materials guide: standard, Somos, clear, tough, castable and high-temp resins, ABS, Nylon PA12 and stainless steel, and what each is best for.',
  heading: '3D Printing Materials',
  eyebrow: 'Materials guide · What we print in, and what each is for',
  answer:
    'This is a guide to the 3D printing materials PrintAWorld prints: six SLA resins (standard, Somos, Somos WaterClear, tough, castable and high-temperature), ABS plastic on our FDM print farm, Nylon PA12, and solid stainless steel by selective laser melting. Each suits different jobs, from fine-detail figurines to clear covers and metal parts. Not sure which one fits? Send the file and tell us what the part is for; we’ll recommend one with the quote.',
  hero: 'samples',
  logos: false,
  reviews: false,
  relatedHeading: 'Our 3D printing services by process',
  faqHeading: 'What do people ask about 3D printing materials?',
  quoteText:
    'Tell us what the part is for, how it’s handled and where it lives. We’ll suggest a material and send a price.',
  reviewedDate,
}

const card = {
  label: 'Reference',
  title: '3D printing materials',
  body: 'Resins, plastics, nylon and stainless steel, and what each is for.',
}

const photos = {
  samples: {
    ...printing.photos.crab,
    title: 'Clear resin crab',
  },
  standard: {
    url: `${photoRoot}/5Tu1s0w2Ee3ValxXhQWBay/53c60e4cf10b904ddcbf0f84e076c041/_DSC8719.jpg`,
    width: 3000,
    height: 1993,
    alt: 'White standard-resin 3D printed miniature figures on a reflective surface',
  },
  somos: { ...printing.photos.architecture },
  clear: { ...sla.photos.mask },
  tough: {
    url: `${photoRoot}/26wqCwNm2Zkr69ZP0v2VG6/4b4f0a44b67eae20fd042fea07a9edf2/_DSC3316.jpg`,
    width: 1500,
    height: 1001,
    alt: 'Blue tough-resin 3D printed test parts',
  },
  heat: { ...sla.photos.heat },
  abs: {
    url: `${photoRoot}/3Uvr953WnUhzbwhAbJAhT6/1b8efc0ed6bb2c47ac891003e2e0b64d/two_hands_holding_a_3d_printed_abs_plastic.png`,
    width: 6520,
    height: 4437,
    alt: 'Two hands holding white and black 3D printed hexagonal ABS containers',
  },
  nylon: {
    url: `${photoRoot}/3FqRNwhMO0TTZsRgA2jgbX/26111574de1eeeac488a3cf60717340e/nylon2.jpg`,
    width: 5535,
    height: 4631,
    alt: 'A hand flexing a white Nylon PA12 3D printed lattice sheet',
  },
  steel: { ...metal.photos.lattice },
}

const intro = {
  eyebrow: 'Choosing a material',
  heading: 'Which 3D printing material should you use?',
  paragraphs: [
    'Start with what the part has to do, not the material name. A figurine needs detail, a bracket needs strength, a lens needs clarity, a mold insert needs heat resistance. Then pick the process that prints that material: resin on SLA printers, plastic on FDM printers, nylon on powder-bed printers, steel on a metal printer.',
    'We review every file before we print, so you don’t have to get this right alone. Tell us how the part is used, handled and where it lives, and the quote names the material and process.',
  ],
}

const glance = {
  heading: 'The 3D printing materials we print, at a glance',
  rows: [
    [
      'SLA resins',
      'Standard, Somos, Somos WaterClear (clear), tough, castable (wax-filled) and high-temperature',
    ],
    [
      'FDM plastic',
      'ABS on our in-house FDM print farm; other plastics on request',
    ],
    ['Nylon', 'Nylon PA12, strong and slightly flexible'],
    ['Metal', 'Stainless steel by selective laser melting (SLM)'],
    [
      'Finishes',
      'Sanding, priming, painting and metal plating (chrome, gold, bronze looks)',
    ],
    [
      'Not offered',
      'Sandstone color prints; metal 3D printing in alloys other than stainless steel',
    ],
    ['Files', files],
    ['Studios', studios],
    ['Delivery', delivery],
  ],
}

const before = [
  {
    id: 'resins',
    eyebrow: 'SLA resin',
    heading: 'Resin 3D printing materials',
    index: '01 / RESIN',
    paragraphs: [
      [
        'Resins print on SLA printers and give the finest detail and smoothest surface. They’re the usual base for painted and plated pieces. See ',
        { to: '/services/sla-3d-printing/', label: 'SLA 3D printing' },
        '.',
      ],
    ],
    materials: [
      {
        title: 'Standard resin',
        process: 'SLA resin',
        body: 'High detail and a smooth surface for figurines, miniatures, models and display pieces. Rigid, and can be brittle if dropped.',
        photo: 'standard',
      },
      {
        title: 'Somos resin',
        process: 'SLA resin',
        body: 'An industrial resin with crisp detail and a clean finish, for presentation prototypes, architectural models and sculptures.',
        photo: 'somos',
      },
      {
        title: 'Somos WaterClear Ultra 10122',
        process: 'SLA resin',
        body: 'A clear, colorless resin that looks like acrylic, with good water and temperature resistance. For lenses, light covers and see-through models.',
        photo: 'clear',
      },
      {
        title: 'Tough resin',
        process: 'SLA resin',
        body: 'For prototypes that get handled, snapped together or tested, where standard resin would crack.',
        photo: 'tough',
      },
      {
        title: 'High-temperature resin',
        process: 'SLA resin',
        body: 'For parts that sit near heat, such as molds and fixtures. Tell us the temperature it has to take.',
        photo: 'heat',
      },
      {
        title: 'Castable resin',
        process: 'SLA resin',
        body: 'A wax-filled resin for casting patterns, such as jewelry. Check burnout and compatibility with your casting house first.',
      },
    ],
  },
  {
    id: 'plastics',
    eyebrow: 'Plastic and nylon',
    heading: 'Plastic and nylon 3D printing materials',
    index: '02 / PLASTIC',
    paragraphs: [
      [
        'Plastics and nylon make tougher working parts and production runs. Large orders run on our in-house FDM print farm. See ',
        { to: '/services/3d-printing/', label: 'online 3D printing' },
        '.',
      ],
    ],
    materials: [
      {
        title: 'ABS',
        process: 'FDM plastic',
        body: 'A strong, tough plastic for mechanical prototypes, enclosures and parts that get handled. For high precision, machining may suit better; for very high volumes, injection molding.',
        photo: 'abs',
      },
      {
        title: 'Nylon PA12',
        process: 'Powder-bed nylon',
        body: 'Strong and slightly flexible, for functional parts, clips and living hinges. The quote confirms the process and availability.',
        photo: 'nylon',
      },
    ],
  },
  {
    id: 'metal',
    eyebrow: 'Metal',
    heading: 'Metal 3D printing material: stainless steel',
    index: '03 / METAL',
    paragraphs: [
      [
        'Stainless steel prints by selective laser melting into a solid metal part, in a 250 × 250 × 300 mm build area. See ',
        { to: '/services/metal-3d-printing/', label: 'metal 3D printing' },
        '.',
      ],
    ],
    materials: [
      {
        title: 'Stainless steel',
        process: 'SLM metal',
        body: 'Solid metal for parts that need strength, heat resistance or the weight and feel of steel, and for shapes that are hard to machine.',
        photo: 'steel',
      },
    ],
  },
]

const contrast = {
  id: 'metal-look',
  eyebrow: 'Gold, silver, chrome?',
  heading: 'Can you 3D print in gold, silver or bronze?',
  paragraphs: [
    'We don’t print solid gold, silver or bronze. For those looks we print in resin and metal plate it: chrome, gold or bronze over the print. Plating coats the surface; the part isn’t solid metal.',
    'If the part has to be solid metal, we print it in stainless steel.',
  ],
  links: [
    {
      to: '/services/custom-3d-printing/',
      label: 'Plated and finished prints',
    },
    { to: '/services/metal-3d-printing/', label: 'Metal 3D printing' },
  ],
}

const table = {
  id: 'compare',
  eyebrow: 'Compare',
  heading: 'Which 3D printing material is best for your part?',
  index: '04 / COMPARE',
  columns: ['Material', 'Process', 'Best for', 'Watch out for'],
  rows: [
    [
      'Standard resin',
      'SLA',
      'Figurines, miniatures, display models',
      'Brittle if dropped or bent',
    ],
    [
      'Somos resin',
      'SLA',
      'Presentation prototypes, architectural models',
      'Not for load-bearing parts',
    ],
    [
      'Somos WaterClear',
      'SLA',
      'Clear covers, lenses, see-through models',
      'Clarity depends on shape and finish',
    ],
    [
      'Tough resin',
      'SLA',
      'Snap fits and handled prototypes',
      'Less detail than standard resin',
    ],
    [
      'High-temperature resin',
      'SLA',
      'Molds and fixtures near heat',
      'Tell us the working temperature',
    ],
    [
      'Castable resin',
      'SLA',
      'Jewelry and casting patterns',
      'Check with your casting house',
    ],
    [
      'ABS',
      'FDM',
      'Tough working parts, enclosures, runs',
      'Layer lines show unless sanded',
    ],
    [
      'Nylon PA12',
      'Powder bed',
      'Clips, hinges, functional parts',
      'Grainy surface; process confirmed in quote',
    ],
    [
      'Stainless steel',
      'SLM metal',
      'Solid metal parts, complex metal shapes',
      'Build area 250 × 250 × 300 mm',
    ],
  ],
  note: 'For outdoor or sunlit parts, tell us: many resins yellow and weaken in sunlight, so another material may suit better.',
}

const checklist = [
  'Your 3D file (STL, OBJ, STEP, STP or 3DM), or photos and a sketch',
  'What the part is for and how it’s handled',
  'Where it lives: indoors, outdoors, near heat, in water',
  'Finished size and how many you need',
  'Material or finish, if you already know it',
  'Your delivery ZIP code and deadline',
]

const faqs = [
  {
    question: 'What materials can be 3D printed?',
    answer:
      'Plastics, resins, nylons and metals are the common groups. At PrintAWorld we print six SLA resins (standard, Somos, Somos WaterClear, tough, castable and high-temperature), ABS on our FDM print farm, Nylon PA12 and stainless steel.',
  },
  {
    question: 'What is the strongest 3D printing material?',
    answer:
      'Of the materials we print, stainless steel, because it’s solid metal. Among plastics, ABS and Nylon PA12 are tougher than standard resin, and tough resin sits in between. Tell us the load and we’ll recommend one.',
  },
  {
    question: 'Which 3D printing material is clear?',
    answer:
      'Somos WaterClear Ultra 10122, a clear, colorless resin that looks like acrylic. Clarity depends on the part’s shape and finish, so say how clear it needs to be.',
  },
  {
    question: 'Is resin or filament better for 3D printing?',
    answer:
      'It depends on the part. Resin (SLA) gives finer detail and a smoother surface, so it suits figurines, models and pieces to paint or plate. Filament (FDM) makes tougher, cheaper parts and large runs. We pick one for your part with the quote.',
  },
  {
    question: 'Which material is best for outdoor 3D prints?',
    answer:
      'Usually ABS or stainless steel, depending on the load. Many resins yellow and weaken in sunlight unless they’re painted and sealed. Tell us where the part will live and we’ll recommend a material and finish.',
  },
  {
    question: 'Are 3D printed parts food safe?',
    answer:
      'Don’t assume they are. Layer lines and some materials aren’t suitable for food contact. Tell us if a part will touch food or skin and we’ll review it; for props that hold drinks we’ve used food-safe inserts.',
  },
  {
    question: 'Can you 3D print in gold or silver?',
    answer:
      'Not as solid metal. We print in resin and add metal plating for gold, silver-tone, chrome or bronze looks. For a solid metal part, we print in stainless steel.',
  },
  {
    question: 'Do you offer sandstone 3D printing?',
    answer:
      'No. For color pieces we print in resin and paint, or quote a part printed in color. See full-color 3D printing.',
  },
]

module.exports = {
  page,
  card,
  photos,
  intro,
  glance,
  before,
  contrast,
  table,
  checklist,
  faqs,
  schema: {
    type: 'WebPage',
    description:
      'A guide to the 3D printing materials PrintAWorld prints, what each suits and what to watch out for: standard, Somos, Somos WaterClear, tough, castable and high-temperature resins, ABS, Nylon PA12 and stainless steel.',
  },
}
